import { usePermissionStore } from '@Models/usePermissionModel';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Collapse,
  Divider,
  Icon,
  MenuItem,
  Stack,
  Text,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { AngleDownIcon, AngleUpIcon } from '@tonic-one/react-icons';
import { useToggle } from '@tonic-one/react-hooks';
import { PermissionNameEnum } from '@Types/permissions';
import { useActiveMenuStyle } from '@Policy/style';
import { useOrgStore } from '../../models/useOrgModel';
import { useQuery } from '@tanstack/react-query';
import { PocApi } from '../../apis';

export const GlobalSettings = ({
  globalSettingsCorrelatedRules,
  globalSettingsUserDefinedLists,
  globalSettingsSettings,
  handleClick,
  selectedKey,
}: {
  handleClick: (url: string) => void;
  selectedKey: string;
  globalSettingsCorrelatedRules: {
    name: string;
    url: string;
    show: boolean;
    shouldBeAllScope: boolean;
  }[];
  globalSettingsUserDefinedLists: {
    name: string;
    url: string;
    show: boolean;
    shouldBeAllScope: boolean;
  }[];
  globalSettingsSettings: {
    name: any;
    url: string;
    show: boolean;
    element: JSX.Element;
    shouldBeAllScope: boolean;
  }[];
}) => {
  const { widthReadablePermission } = usePermissionStore();
  const { t } = useTranslation();
  const [colorMode] = useColorMode();
  const [isExpand, toggleValue] = useToggle(true);
  const [colorStyle] = useColorStyle({ colorMode });
  if (!widthReadablePermission(PermissionNameEnum.administrator)) {
    return <></>;
  }
  return (
    <Box>
      <Divider width="100%" />
      <MenuItem onClick={toggleValue} py="12px" mb="8px">
        <Text size={12} color={colorStyle.color.secondary} fontWeight={600}>
          {t('policy.globalSettingsTitle')}
        </Text>
        {isExpand ? (
          <Icon as={AngleUpIcon} marginLeft="auto" />
        ) : (
          <Icon as={AngleDownIcon} marginLeft="auto" />
        )}
      </MenuItem>
      <Collapse in={isExpand} unmountOnExit>
        <Stack gap="2x">
          <CorrelatedRules
            globalSettingsCorrelatedRules={globalSettingsCorrelatedRules}
            handleClick={handleClick}
            selectedKey={selectedKey}
          />
          <UserDefineList
            globalSettingsUserDefinedLists={globalSettingsUserDefinedLists}
            handleClick={handleClick}
            selectedKey={selectedKey}
          />
          <OtherGlobalSettings
            globalSettingsSettings={globalSettingsSettings}
            handleClick={handleClick}
            selectedKey={selectedKey}
          />
        </Stack>
      </Collapse>
    </Box>
  );
};

const CorrelatedRules = ({
  globalSettingsCorrelatedRules,
  handleClick,
  selectedKey,
}: {
  globalSettingsCorrelatedRules: {
    name: string;
    url: string;
    show: boolean;
    shouldBeAllScope: boolean;
  }[];
  handleClick: (url: string) => void;
  selectedKey: string;
}) => {
  const { managementScopeIds } = usePermissionStore();
  const { t } = useTranslation();
  const [colorMode] = useColorMode();
  const activeMenuStyle = useActiveMenuStyle();
  const [colorStyle] = useColorStyle({ colorMode });
  return (
    <Box>
      <Text pb="8px" px="12px" size={12} color={colorStyle.color.secondary}>
        {t('policy.globalSettings.correlatedRules')}
      </Text>
      {globalSettingsCorrelatedRules.map(
        (policy) =>
          policy.show &&
          (policy.shouldBeAllScope ? managementScopeIds === 'all' : true) && (
            <MenuItem
              {...(selectedKey === policy.url ? activeMenuStyle : {})}
              key={policy.url}
              onClick={() => handleClick(policy.url)}
            >
              <Text pl={12}>{policy.name}</Text>
            </MenuItem>
          )
      )}
    </Box>
  );
};

const UserDefineList = ({
  globalSettingsUserDefinedLists,
  handleClick,
  selectedKey,
}: {
  globalSettingsUserDefinedLists: {
    name: string;
    url: string;
    show: boolean;
    shouldBeAllScope: boolean;
    key?: string;
  }[];
  handleClick: (url: string) => void;
  selectedKey: string;
}) => {
  const { managementScopeIds } = usePermissionStore();
  const { t } = useTranslation();
  const [colorMode] = useColorMode();
  const activeMenuStyle = useActiveMenuStyle();
  const [colorStyle] = useColorStyle({ colorMode });
  const { orgId } = useOrgStore();

  const { data: isPoc } = useQuery({
    initialData: false,
    queryKey: ['apiCheckIsPoc', orgId],
    queryFn: async () => {
      return await PocApi.apiCheckIsPoc(orgId);
    },
  });

  if (managementScopeIds !== 'all') {
    return <></>;
  }

  return (
    <Box>
      <Text pb="8px" px="12px" size={12} color={colorStyle.color.secondary}>
        {t('policy.globalSettings.userDefinedLists')}
      </Text>
      {globalSettingsUserDefinedLists
        // conditional shows the pocUserManagement menu
        .filter((item) => item.key !== 'pocUserManagement' || isPoc)
        .map(
          (policy) =>
            policy.show &&
            (policy.shouldBeAllScope ? managementScopeIds === 'all' : true) && (
              <MenuItem
                {...(selectedKey === policy.url ? activeMenuStyle : {})}
                key={policy.url}
                onClick={() => handleClick(policy.url)}
              >
                <Text pl={12}>{policy.name}</Text>
              </MenuItem>
            )
        )}
    </Box>
  );
};

const OtherGlobalSettings = ({
  globalSettingsSettings,
  handleClick,
  selectedKey,
}: {
  globalSettingsSettings: {
    name: any;
    url: string;
    show: boolean;
    element: JSX.Element;
    shouldBeAllScope: boolean;
  }[];
  handleClick: (url: string) => void;
  selectedKey: string;
}) => {
  const { managementScopeIds } = usePermissionStore();
  const { t } = useTranslation();
  const [colorMode] = useColorMode();
  const activeMenuStyle = useActiveMenuStyle();
  const [colorStyle] = useColorStyle({ colorMode });
  return (
    <Box>
      <Text pb="8px" px="12px" size={12} color={colorStyle.color.secondary}>
        {t('policy.globalSettings.settings.title')}
      </Text>
      {globalSettingsSettings.map(
        (policy) =>
          policy.show &&
          (policy.shouldBeAllScope ? managementScopeIds === 'all' : true) && (
            <MenuItem
              {...(selectedKey === policy.url ? activeMenuStyle : {})}
              key={policy.url}
              onClick={() => handleClick(policy.url)}
            >
              <Text pl={12}>{policy.name}</Text>
            </MenuItem>
          )
      )}
    </Box>
  );
};
